import React, { useRef, forwardRef, useState, useMemo } from 'react'
import { pick, castArray, keys } from 'lodash-es'
import { Button, Popup, Modal } from '@ow/comps'
import { CloseIcon } from '@ow/icons'
import { WebBasicForm, WebBasictable, WebBasicLayouts } from '@ow/ditto'
import { dealLayoutSlugs, dealRefTypeFilter } from '@ow/ditto-core'
import { useFetchRemoteData, useTbViewStore } from '@vt/hooks'
import { showNotification } from '@mantine/notifications'
import RepeatVerify from './RepeatVerify'
import widgets from '../widgets'

const Footer = ({ onClose, onConfirm, submitting }) => {
  return (
    <div className="flex gap-2">
      <Button
        type="secondary"
        opacityType="sm"
        onClick={onClose}
        className="dark:bg-darkTheme-800 dark:border-darkTheme-1100 dark:text-dark-200"
      >
        取消
      </Button>
      <Button
        type="primary"
        onClick={() => {
          onConfirm(onClose)
        }}
      >
        {submitting ? '正在提交...' : '确定'}
      </Button>
    </div>
  )
}

// 编辑字段无需判重
const OperateModal = (
  {
    trigger,
    title,
    isDuplicate,
    data,
    isBatch,
    fieldsLayouts,
    onCancel,
    onConfirm,
    detailData,
  },
  ref
) => {
  const formRef = useRef(null)
  const verifyRef = useRef(null)
  const [isReplace, setIsReplace] = useState(false)
  const [verifyPass, setVerifyPass] = useState(false)
  const [defaultValues, setDefaultValues] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const {
    exporterConfig,
    standardConfig,
    collectorConfig,
    matrixConfig,
    fields,
    duplicateConfig,
  } = useTbViewStore((state) =>
    pick(state, [
      'exporterConfig',
      'collectorConfig',
      'matrixConfig',
      'fields',
      'standardConfig',
      'duplicateConfig',
    ])
  )

  const { filterScope } = standardConfig || {}

  // 字段间逻辑关系
  const { customFileds } = collectorConfig || {}
  const conditions = (collectorConfig && collectorConfig.conditions) || {}
  const isPassValidation =
    (collectorConfig && collectorConfig.isPassValidation) || false
  const repeatVerifiyfilters =
    collectorConfig && collectorConfig.repeatVerifiyfilters
  const needRepeatVerifiySlugs =
    (repeatVerifiyfilters &&
      (repeatVerifiyfilters.queries || []).map((v) => v.slug)) ||
    []
  const isverifyPass = verifyPass || !repeatVerifiyfilters || data

  // 大表相关
  const showTableLayouts = matrixConfig && matrixConfig.allow
  const headerFieldSlugs = matrixConfig && castArray(matrixConfig.headerSlug)

  const fetchRemoteData = useFetchRemoteData()

  const { waterMarkDefs } = exporterConfig || {}

  // 整合字段逻辑关系、字段判重
  const conditionedFields = useMemo(() => {
    const arr = fields
      .map((x) => {
        if (conditions[x.slug]) {
          return {
            ...x,
            logicSpec: conditions[x.slug],
          }
        }
        return x
      })
      .map((x) => {
        if (needRepeatVerifiySlugs.includes(x.slug)) {
          return { ...x, readonly: true }
        }
        return x
      })

    if (duplicateConfig && isDuplicate) {
      const { editableSlugs = [] } = duplicateConfig
      return arr.filter((v) => editableSlugs.includes(v.slug))
    }
    if (customFileds) {
      return arr.map((v) => {
        const obj = customFileds[v.slug]
        return obj ? { ...v, ...obj } : v
      })
    }
    return arr
  }, [fields, conditions, needRepeatVerifiySlugs, defaultValues])

  console.log(conditionedFields, 'conditionedFields')

  const initStateData = () => {
    setIsReplace(false)
    setVerifyPass(false)
    setSubmitting(false)
  }

  const modalClose = () => {
    if (ref && ref.current) {
      ref.current.close()
    }
    if (onCancel) {
      onCancel()
    }
    initStateData()
  }

  // 数据校验方法
  const verifyData = () => {
    if (verifyRef && verifyRef.current) {
      verifyRef.current.verifyData()
    }
  }

  const finalData = useMemo(() => {
    return verifyRef && verifyRef.current
      ? { ...data, ...verifyRef.current.getValues() }
      : data
  }, [data])

  const showFormData = useMemo(() => {
    return isReplace
      ? (verifyRef && verifyRef.current && verifyRef.current.repeatData) || {}
      : finalData
  }, [isReplace, finalData])

  // 最终数据提交(需根据是否是批量创建判断是否校验通过)
  const onSubmit = async (afterSubmit) => {
    if (formRef && formRef.current) {
      // 只验证当前表单
      if (!isBatch) {
        formRef.current.trigger().then((tr) => {
          console.log('data validate:', tr)
          if (tr) {
            const obj = formRef.current.getValues()
            const { slugs } = formRef.current
            if (!submitting) {
              setSubmitting(true)
              onConfirm(
                obj.objectId ? pick(obj, [...slugs, 'objectId']) : obj,
                afterSubmit
              )
            }
          }
          return null
        })
      } else {
        // 当前表与关联表一起新建
        const refSlugs = fields
          .filter((v) => v.type === 'ref')
          .map((v) => v.slug)
        const promiseArr = []
        const values = {}
        const currentFormData = formRef.current.getValues()
        refSlugs.forEach((v) => {
          const arr = keys(currentFormData)
          if (arr.includes(v)) {
            const refFormRef = currentFormData[v]
            if (refFormRef && refFormRef.current) {
              promiseArr.push(refFormRef.current.trigger())
              values[v] = refFormRef.current.getValues()
            }
          }
        })

        return Promise.all([...promiseArr, formRef.current.trigger()]).then(
          (trs) => {
            if (trs.includes(false)) {
              showNotification({
                title: '请确保数据填写正确后提交',
              })
            } else {
              const obj = formRef.current.getValues()
              if (!submitting) {
                setSubmitting(true)
                onConfirm(obj, afterSubmit, values)
              }
            }
          }
        )
      }
    }
    return null
  }

  const onClickConfirm = (afterSubmit) => {
    if (isverifyPass) {
      // 如果通过了，就说明是提交数据
      onSubmit(afterSubmit)
    } else {
      // 如果没有通过，就说明是校验，需判重
      verifyData()
    }
  }

  // 编辑 新建
  if (fieldsLayouts || showTableLayouts) {
    return (
      <Popup
        ref={ref}
        modal
        closeOnDocumentClick={false}
        contentStyle={{
          height: '100%',
          marginTop: '30px',
          borderRadius: '3px',
        }}
        trigger={trigger}
      >
        <div
          className="flex flex-col relative"
          style={{
            width: '85vw',
            height: 'calc(100vh - 60px)',
            background: '#fff',
            borderRadius: '3px',
          }}
        >
          <div
            style={{
              fontSize: '1rem',
              fontWeight: 'bold',
              borderBottom: '1px solid #E7E7E7',
              color: 'rgba(0,0,0,0.90)',
              height: '56px',
            }}
            className="w-full flex items-center px-4"
          >
            {title}
            <div
              className="absolute right-4 cursor-pointer z-10"
              onClick={modalClose}
            >
              <CloseIcon width={18} height={18} fill="currentColor" />
            </div>
          </div>
          <div
            style={{ height: 'calc(100% - 120px)' }}
            className="overflow-hidden"
          >
            {!verifyPass && repeatVerifiyfilters && !data ? (
              <RepeatVerify
                ref={verifyRef}
                updateVerifyResult={setVerifyPass}
                updateIsReplace={setIsReplace}
                updateDefaultValues={setDefaultValues}
              />
            ) : (
              <div className="h-full flex">
                {showTableLayouts ? (
                  <WebBasictable
                    passValidation={isPassValidation}
                    headerFields={fields.filter((v) =>
                      headerFieldSlugs.includes(v.slug)
                    )}
                    fields={conditionedFields}
                    config={collectorConfig}
                    waterMarkDefs={waterMarkDefs}
                    onFetchRemoteData={fetchRemoteData}
                    data={showFormData}
                    defaultSlugsValues={defaultValues}
                    filterScope={filterScope}
                    widgets={widgets()}
                    ref={formRef}
                    detailData={detailData}
                  />
                ) : (
                  <WebBasicLayouts
                    fields={conditionedFields}
                    isReCreate
                    editableSlugs={dealLayoutSlugs(fieldsLayouts)}
                    refTypeFilterMap={dealRefTypeFilter(fieldsLayouts)} // 关联表的操作可能会用到默认筛选（湛江）
                    layouts={fieldsLayouts}
                    onFetchRemoteData={fetchRemoteData}
                    originalData={showFormData}
                    defaultSlugsValues={defaultValues}
                    widgets={widgets()}
                    data={showFormData}
                    ref={formRef}
                    detailData={detailData}
                    waterMarkDefs={waterMarkDefs}
                  />
                )}
              </div>
            )}
          </div>
          <div className="absolute bottom-0 right-0 p-4 h-14">
            <Footer
              onClose={modalClose}
              onConfirm={onClickConfirm}
              submitting={submitting}
            />
          </div>
        </div>
      </Popup>
    )
  }

  return (
    <Modal
      title={title}
      ref={ref}
      nested
      size="lg"
      showCloseButton
      closeOnDocumentClick={false}
      contentStyle={{ height: '35rem' }}
      trigger={trigger}
      isFooterComp
      footer={
        <Footer
          onClose={modalClose}
          onConfirm={onClickConfirm}
          submitting={submitting}
        />
      }
    >
      {!verifyPass && repeatVerifiyfilters ? (
        <RepeatVerify
          ref={verifyRef}
          updateVerifyResult={setVerifyPass}
          updateIsReplace={setIsReplace}
        />
      ) : (
        <WebBasicForm
          fields={conditionedFields}
          data={showFormData}
          defaultSlugsValues={defaultValues}
          config={collectorConfig}
          waterMarkDefs={waterMarkDefs}
          onFetchRemoteData={fetchRemoteData}
          widgets={widgets()}
          ref={formRef}
        />
      )}
    </Modal>
  )
}
export default forwardRef(OperateModal)
